import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { TableRow, TableCell } from '@mui/material';
import TableComponent from '../../../components/Table/TableComponent';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import SearchIcon from '@mui/icons-material/Search';
import ChartButton from '../../../components/Button/ChartButton'
import IconButton from '../../../components/Buttons/IconButton'
import Chart from '../../../components/Chart';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import { ClearButton } from '../../../components/Button/iconbutton'
import { ObjectiveResponse, UrineAnalysis } from '../../../components/interface/ObjectiveInterface';


const UrinalysisForm: React.FC<{ patientId: string }> = ({ patientId }) => {
    const [vitalRecords, setVitalRecords] = useState<UrineAnalysis[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [editRecord, setEditRecord] = useState<UrineAnalysis | null>(null);
    const [showChart, setShowChart] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
    const [showClearFilterButton, setShowClearFilterButton] = useState(false)

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };
 
    const getportionColor = (glucoseValue: string) => {
        const sanitizedValue = glucoseValue.trim(); // Remove extra spaces
        switch (sanitizedValue) {
            case "Negative":
                return "#008000"; // Green
            case "Trace":
                return "#cdfa02"; // Light Blue
            case "Small (1+) (30 mg/dL)":
                return "#fff707"; // Yellow
            case "Moderate (2+) (100 mg/dL)":
                return "#FFC107"; // Orange
            case "Large (3+) (300 mg/dL)":
                return "#FF0000"; // Red
            case "Very Large (4+) (2000 mg/dL)":
                return "#8B0000"; // Dark Red
            default:
                return "transparent"; // Fallback color if no match
        }
    };

    const getGlucoseColor = (glucoseValue: string) => {
        const sanitizedValue = glucoseValue.trim(); // Remove extra spaces
        switch (sanitizedValue) {
            case "Negative":
                return "#008000"; // Green
            case "Trace":
                return "#cdfa02"; // Light Blue
            case "Small (1+) (100 mg/dL)":
                return "#fff707"; // Yellow
            case "Moderate (2+) (250 mg/dL)":
                return "#FFC107"; // Orange
            case "Large (3+) (500 mg/dL)":
                return "#FF0000"; // Red
            case "Very Large (4+) (1000 mg/dL)":
                return "#8B0000"; // Dark Red
            default:
                return "transparent"; // Fallback color if no match
        }
    };

    const getLeukocytesColor = (leukocytesValue: string) => {
        const sanitizedValue = leukocytesValue.trim(); // Remove extra spaces
        switch (sanitizedValue) {
            case "Negative":
                return "#008000"; // Green
            case "Trace":
                return "#cdfa02"; // Light Blue
            case "Small (1+) mg/dL":
                return "#fff707"; // Yellow
            case "Moderate (2+) mg/dL":
                return "#FFC107"; // Orange
            case "Large (3+) mg/dL":
                return "#FF0000"; // Red 
            default:
                return "transparent";
        }
    };
    const getPHColor = (phValue: number) => {
        if (phValue < 6) {
            return '#FF0000'; // Red for acidic
        } else if (phValue >= 6 && phValue <= 7) {
            return '#008000'; // Green for normal
        } else {
            return '#0000FF'; // Blue for alkaline
        }
    };


    useEffect(() => {
        fetchUrinalyses();
        setShowClearFilterButton(!!(searchTerm !== '' || (dateRange.startDate && dateRange.endDate)));
    }, [page, rowsPerPage, searchTerm, dateRange]);

    const fetchUrinalyses = async (startDate: string = '', endDate: string = '') => {
        setLoading(true);
        try {
            const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {

                params: {
                    limit: rowsPerPage,
                    page: page + 1,
                    from: startDate || '',
                    to: endDate || '',
                    order_by: 'details->date',
                    dir: 2,
                    user_id: patientId,
                    slug: 'urine',
                    searchkey: searchTerm || undefined,
                },
                headers: getAxiosConfig().headers,
            }
            );

            const { vitals } = response.data.data
            const total = response.data.data.pagination!.total;

            // Map vitals to Urinalysis format
            if (Array.isArray(vitals) && vitals.length > 0) {
                const formattedData: UrineAnalysis[] = vitals.map((vital) => ({
                    id: vital.id,
                    user_id: vital.user_id,
                    consult_id: vital.consult_id,
                    peripheral_id: vital.peripheral_id,
                    slug: 'urine',
                    details: {
                        ph: vital.details.ph, // Ensure this is correctly mapped
                        date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
                        time: vital.details.time,
                        color: vital.details.color,
                        urine: vital.details.urine,
                        value: vital.details.value,
                        clarity: vital.details.clarity,
                        crystal: vital.details.crystal,
                        glucose: vital.details.glucose,
                        ketones: vital.details.ketones,
                        protein: vital.details.protein,
                        nitrites: vital.details.nitrites,
                        rbc_flag: vital.details.rbc_flag,
                        bilirubin: vital.details.bilirubin,
                        leukocytes: vital.details.leukocytes,
                        sugar_flag: vital.details.sugar_flag,
                        value_flag: vital.details.value_flag,
                        rbc_message: vital.details.rbc_message,
                        protein_flag: vital.details.protein_flag,
                        urobilinogen: vital.details.urobilinogen,
                        epithelialCells: vital.details.epithelialCells,
                        sugar_message: vital.details.sugar_message,
                        blood_in_urine: vital.details.blood_in_urine,
                        whiteBloodCells: vital.details.whiteBloodCells,
                        microorganism: vital.details.microorganism,
                        redBloodCells: vital.details.redBloodCells,
                        rbc_range_code: vital.details.rbc_range_code,
                        leukocytes_flag: vital.details.leukocytes_flag,
                        protein_message: vital.details.protein_message,
                        specific_gravity: vital.details.specific_gravity, // Default to 0 if missing
                        sugar_range_code: vital.details.sugar_range_code,
                        value_range_code: vital.details.value_range_code,
                        leukocytes_message: vital.details.leukocytes_message,
                        protein_range_code: vital.details.protein_range_code,
                        leukocytes_range_code: vital.details.leukocytes_range_code,
                    },
                    freeze: vital.freeze,
                    document: null,
                }));

                setVitalRecords(formattedData);
                setTotalRecords(total ?? 0);
            } else {
                setVitalRecords([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching vital records:', error);
            setVitalRecords([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setEditRecord(null);
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        fetchUrinalyses();
        fetchUrinalyses(dateRange.startDate, dateRange.endDate);
    };

    const handleDialogSave = (newRecord: any) => {
        try {
            if (editRecord) {
                setVitalRecords(prev => prev.map(record => (record.id === editRecord.id ? newRecord : record)));
                setSnackbarMessage('UrineAnalysis updated successfully!');
            } else {
                setVitalRecords(prev => [...prev, newRecord]);
                setSnackbarMessage('UrineAnalysis added successfully!');
            }
            setSnackbarSeverity('success');
            setSnackbarOpen(true); // Show the snackbar
        } catch (error) {
            console.error('Error saving UrineAnalysis:', error);
            setSnackbarMessage('Failed to save UrineAnalysis.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true); // Show the snackbar for error
        } finally {
            setOpenDialog(false);
            fetchUrinalyses(); // Fetch updated list
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ startDate: '', endDate: '' });
        setPage(0);
        setRowsPerPage(5);
        fetchUrinalyses();
        setShowClearFilterButton(false);
    };

    // Render each row in the table
    const renderRow = (item: UrineAnalysis) => {

        const leukocytesDisplay = (value: string) => {
            switch (value) {
                case 'Negative':
                    return 'Negative';
                case 'Trace':
                    return 'Trace';
                case 'Small (1+) mg/dL':
                    return '+';
                case 'Moderate (2+) mg/dL':
                    return '++';
                case 'Large (3+) mg/dL':
                    return '+++';
                default:
                    return value;
            }
        };

        const portionDisplay = (value: string) => {
            switch (value) {
                case 'Negative':
                    return 'Negative';
                case 'Trace':
                    return 'Trace';
                case 'Small (1+) (30 mg/dL)':
                    return '+';
                case 'Moderate (2+) (100 mg/dL)':
                    return '++';
                case 'Large (3+) (300 mg/dL)':
                    return '+++';
                case 'Very Large (4+) (2000 mg/dL)':
                    return '++++';
                default:
                    return value;
            }
        };
        const glucoseDisplay = (value: string) => {
            switch (value) {
                case 'Negative':
                    return 'Negative';
                case 'Trace':
                    return 'Trace';
                case 'Small (1+) (100 mg/dL)':
                    return '+';
                case 'Moderate (2+) (250 mg/dL)':
                    return '++';
                case 'Large (3+) (500 mg/dL)':
                    return '+++';
                case 'Very Large (4+) (1000 mg/dL)':
                    return '++++';
                default:
                    return value;
            }
        };

        return (
            <TableRow key={item.id}>
                {/* <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{index + 1}</TableCell> */}
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{`${item.details.date} ${item.details.time}`}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.color}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.clarity}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                    <span
                        style={{
                            backgroundColor: getPHColor(item.details.ph ?? '0'),
                            padding: '2px 4px',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            display: 'inline-block',
                            width: 'fit-content'
                        }}
                    >
                        {item.details.ph ?? 'N/A'}
                    </span>
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>

                    <span
                        style={{
                            backgroundColor: getportionColor(item.details.protein ?? ''),
                            padding: '4px',
                            color: '#FFFFFF',
                            borderRadius: '6px',
                            display: 'inline-block',
                        }}
                    >
                        {portionDisplay(item.details.protein ?? 'N/A')}
                    </span>
                </TableCell>


                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                    <span
                        style={{
                            backgroundColor:getGlucoseColor(item.details.glucose ?? ''),
                            padding: '4px',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            display: 'inline-block',
                        }}
                    >
                        {glucoseDisplay(item.details.glucose ?? 'N/A')}
                    </span>
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>

                    <span
                        style={{
                            backgroundColor: getLeukocytesColor(item.details.leukocytes ?? ''),
                            padding: '4px',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            display: 'inline-block',
                        }}
                    >
                        {leukocytesDisplay(item.details.leukocytes ?? 'N/A')}
                    </span>
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.bilirubin}</TableCell>
            </TableRow>
        );
    };

    const chartData = {
        labels: vitalRecords.map((record) => record.details.date),
        datasets: [
            {
                label: 'pH',
                data: vitalRecords.map((record) => {
                    const value = record.details.ph;
                    return typeof value === 'number' ? value : 0; // Default to 0 or handle it as needed
                }),
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
            },
            {
                label: 'Specific Gravity',
                data: vitalRecords.map((record) => {
                    const value = record.details.specific_gravity;
                    return typeof value === 'number' ? value : 0; // Ensure it's a number
                }),
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
            },
            {
                label: 'Urobilinogen',
                data: vitalRecords.map((record) => {
                    const value = record.details.urobilinogen;
                    return typeof value === 'number' ? value : 0; // Ensure it's a number
                }),
                backgroundColor: 'rgba(255, 206, 86, 0.5)',
                borderColor: 'rgba(255, 206, 86, 1)',
                borderWidth: 1,
            },
            {
                label: 'Red Blood Cells',
                data: vitalRecords.map((record) => {
                    const value = record.details.redBloodCells;
                    return typeof value === 'number' ? value : 0; // Ensure it's a number
                }),
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            },
            {
                label: 'White Blood Cells',
                data: vitalRecords.map((record) => {
                    const value = record.details.whiteBloodCells;
                    return typeof value === 'number' ? value : 0; // Ensure it's a number
                }),
                backgroundColor: 'rgba(153, 102, 255, 0.5)',
                borderColor: 'rgba(153, 102, 255, 1)',
                borderWidth: 1,
            },
        ],
    };


    const chartOptions = {
        scales: {
            y: {
                grid: {
                    display: false, // Disable the grid lines on the y-axis
                },
            },
            x: {
                grid: {
                    display: false, // Disable the grid lines on the x-axis
                },
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
            },
        },
    };

    console.log(vitalRecords);

    return (
        <div>
            <div className="flex items-end justify-between w-full gap-12">
                <div className="flex flex-grow space-x-2 gap-2">
                    <DateRangePicker
                        startDateId={'Vitals_Start_Date'}
                        endDateId={'Vitals_End_Date'}
                        onChange={(dates: { startDate: string; endDate: string }) => {
                            setDateRange(dates)
                        }}
                        value={dateRange} // Pass the selected date range
                    />

                    <IconButton
                        icon={<SearchIcon />}
                        onClick={() => fetchUrinalyses(dateRange.startDate, dateRange.endDate)}
                    />
                    {showClearFilterButton && <ClearButton onClick={handleClearFilters} />}

                </div>
                <div className="flex justify-end space-x-4">
              
                    <ChartButton text={showChart ? 'List' : 'Chart'} onClick={() => setShowChart(prev => !prev)} />
                   </div>
            </div>

            {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
                    <TableComponent
                        headers={['S.No', 'Date & Time', 'Color', 'Clarity', 'pH', 'Protein', 'Glucose', 'Leukocyte', 'Bilirubin']}
                        data={vitalRecords}
                        loading={loading}
                        renderRow={renderRow} error={''} />
                    {vitalRecords.length === 0 && (
                        <div className="text-center my-4"></div>
                    )}
                </>
            )}
            <TablePaginationComponent
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />

        </div>
    );
};

export default UrinalysisForm;
